/**
 * The app's one market data socket.
 *
 * A provider rather than a module singleton because the socket is a resource
 * with a lifetime, and a test that mounts a component should get its own.
 * `crashReporter()` next door is a lazy singleton and is right to be — it is
 * a sink with no state anyone reads back. This is the opposite.
 *
 * ## Why one socket, and not one per chart
 *
 * A workspace can hold four charts on four symbols. The obvious shape — each
 * pane opens its own feed — multiplies sockets by panes, and CLAUDE.md's
 * licensing constraints run the other way: a user's devices are fed from a
 * single upstream session held server-side, and entitlements are evaluated per
 * message in the fan-out. Four sockets asking for four symbols is four times
 * the connection state to reason about for data that arrives on one.
 *
 * So the socket is here, the routing is in `FeedRouter`, and a pane says only
 * which symbol it wants. `useSymbolFeed` is the whole surface a chart needs.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Feed, feedUrl, type ConnectionState } from './ws.js';
import { FeedRouter, type SymbolHandlers } from './feedRouter.js';
import { crashReporter } from './reporting.js';

export interface MarketFeed {
  /** The socket's state, for the connection badge. */
  readonly connection: ConnectionState;
  /** The last error the server sent, or null. */
  readonly error: string | null;
  readonly clearError: () => void;
}

interface FeedContext extends MarketFeed {
  readonly router: FeedRouter;
}

const Ctx = createContext<FeedContext | null>(null);

export function MarketFeedProvider({ children }: { children: ReactNode }): JSX.Element {
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [error, setError] = useState<string | null>(null);

  const feed = useRef<Feed | null>(null);

  /**
   * Built during render, not in the effect below.
   *
   * A child's effect runs BEFORE its parent's. A pane subscribing on mount
   * would find no router if this waited for the provider's effect, and would
   * silently render a chart that never ticks. Lazy-initialising a ref is the
   * documented way to make a mutable value exist from the first render.
   *
   * The feed it talks to does not exist yet, which is why `onSymbolsChanged`
   * goes through the ref: early announcements no-op, and the effect seeds the
   * feed with whatever accumulated before connecting.
   */
  const router = useRef<FeedRouter | null>(null);
  if (!router.current) {
    router.current = new FeedRouter({
      onSymbolsChanged: (symbols) => feed.current?.setSymbols([...symbols]),
      onHandlerError: (thrown, symbol) => {
        // Reported rather than swallowed. `FeedRouter` keeps the other panes
        // ticking, so without this the only symptom is one chart that stopped
        // for no stated reason.
        const reporter = crashReporter();
        reporter.breadcrumb('feed', `handler threw for ${symbol}`);
        void reporter.report(thrown, 'handled');
      },
    });
  }
  const routed = router.current;

  useEffect(() => {
    const f = new Feed(feedUrl(), {
      onState: setConnection,
      onError: (m) => setError(m.message),
      // Everything symbol-shaped goes to the router, which is the only thing
      // that knows who asked for what.
      onTick: (m) => routed.deliver(m),
      onBar: (m) => routed.deliver(m),
      onAnalysis: (m) => routed.deliver(m),
      onStatus: (m) => routed.deliver(m),
    });
    feed.current = f;
    // Panes that mounted before this effect ran are already in the router, and
    // their announcements went nowhere. `connect` sends whatever set the feed
    // holds when the socket opens, so seeding it here is what makes those
    // subscriptions real.
    f.setSymbols([...routed.symbols()]);
    f.connect();
    return () => {
      f.close();
      feed.current = null;
    };
  }, [routed]);

  const value = useMemo<FeedContext>(
    () => ({ connection, error, clearError: () => setError(null), router: routed }),
    [connection, error, routed],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useFeedContext(): FeedContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMarketFeed must be used inside <MarketFeedProvider>');
  return ctx;
}

/** The connection state and last error, for whatever displays them. */
export function useMarketFeed(): MarketFeed {
  const { connection, error, clearError } = useFeedContext();
  return useMemo(() => ({ connection, error, clearError }), [connection, error, clearError]);
}

/**
 * Receive one symbol's messages for as long as this component is mounted.
 *
 * `handlers` may be a fresh object on every render — it is read through a ref,
 * so a caller does not have to memoise anything to avoid resubscribing. Only a
 * change of `symbol` unsubscribes and resubscribes, which is the one case where
 * that is what you want.
 */
export function useSymbolFeed(symbol: string, handlers: SymbolHandlers): void {
  const { router } = useFeedContext();

  const latest = useRef(handlers);
  // Declared before the subscribing effect so it has run by the time any
  // message can arrive. Deliberately without a dependency array: the point is
  // to track the newest handlers, whatever caused the render.
  useEffect(() => { latest.current = handlers; });

  useEffect(() => router.subscribe(symbol, {
    onTick: (m) => latest.current.onTick?.(m),
    onBar: (m) => latest.current.onBar?.(m),
    onAnalysis: (m) => latest.current.onAnalysis?.(m),
    onStatus: (m) => latest.current.onStatus?.(m),
  }), [router, symbol]);
}
